import { useState } from "react";
import { ContactFormSchema } from "../components/contact-form/contactFormSchema";
import { getFormattedErrorMessage } from "../components/contact-form/utils/errorHandling";
import { QuestionnaireAnswers } from "../components/contact-form/utils/questionnaireStorage";
import { Country } from "../utils/countryUtils";
import {
  checkZipCodeEligibility,
  isUnderEighteen,
} from "../utils/validationUtils";
import { useContactFormSubmission } from "./useContactFormSubmission";

interface UseContactFormProps {
  questionnaireAnswers: QuestionnaireAnswers;
  landingPageId: string;
  selectedCountry: Country;
  isDeferredEmrConsent?: boolean;
}

export function useContactForm({
  questionnaireAnswers,
  landingPageId,
  selectedCountry,
  isDeferredEmrConsent,
}: UseContactFormProps) {
  const [showAgeModal, setShowAgeModal] = useState(false);
  const [isZipCodeValid, setIsZipCodeValid] = useState<boolean | undefined>(
    undefined,
  );
  const [formDataForSubmission, setFormDataForSubmission] =
    useState<ContactFormSchema | null>(null);

  const { submitForm, isSubmitting, isSubmitted, submissionError } =
    useContactFormSubmission({
      questionnaireAnswers,
      landingPageId,
      isDeferredEmrConsent,
    });

  // Simple function to check ZIP code eligibility
  const checkZipCodeAndSetEligibility = (zipCode: string) => {
    // Call the utility function with the selected country code
    const eligibilityResult = checkZipCodeEligibility(
      zipCode,
      selectedCountry.code,
    );
    setIsZipCodeValid(eligibilityResult.isValid);
    return eligibilityResult.isValid;
  };

  // Submit handler with age verification
  const handleSubmit = async (data: ContactFormSchema) => {
    setFormDataForSubmission(data);

    if (data.dateOfBirth && isUnderEighteen(data.dateOfBirth)) {
      setShowAgeModal(true);
      return;
    }

    await submitForm(data);
  };

  const handleAgeModalContinue = async () => {
    setShowAgeModal(false);

    if (formDataForSubmission) {
      await submitForm(formDataForSubmission);
    }
  };

  const handleAgeModalClose = () => {
    setShowAgeModal(false);
  };

  return {
    isSubmitting,
    isSubmitted,
    showAgeModal,
    isZipCodeValid,
    setIsZipCodeValid,
    formattedErrorMessage: submissionError
      ? getFormattedErrorMessage(submissionError)
      : null,
    submissionError,
    handleSubmit,
    handleAgeModalContinue,
    handleAgeModalClose,
    checkZipCodeAndSetEligibility,
  };
}
